import { Button, ButtonProps } from '@chakra-ui/react'
import React, { FC, ReactNode } from 'react'
import Spinner from '../Spinner'

interface DefaultButtonProps extends ButtonProps {
  text?: string
  loading?: boolean
  loadingText?: string
  icon?: ReactNode
  buttonType?: 'primary' | 'secondary' | 'danger'
  disabled?: boolean
  children?: ReactNode
}

type ButtonDefaultSettings = {
  bg?: string
  color?: string
  border?: string
  cursor?: string
  opacity?: number
  transition?: string
  transform?: string
  _hover?: Record<string, any>
  _disabled?: Record<string, any>
}

const DefaultButton: FC<DefaultButtonProps> = ({
  text,
  rounded,
  icon,
  children,
  borderRadius,
  loading,
  loadingText = 'Loading...',
  onClick,
  disabled,
  buttonType,
  className,
  isLoading,
  ...rest
}: DefaultButtonProps) => {
  const isButtonLoading = Boolean(loading || isLoading)
  const isDisabled = Boolean(disabled || isButtonLoading)
  const clickFn = isDisabled ? undefined : onClick
  let buttonDefaults: ButtonDefaultSettings = {}

  if (isDisabled) {
    buttonDefaults.cursor = 'not-allowed'
    buttonDefaults.opacity = 1
    buttonDefaults.transition = 'none'
    buttonDefaults.transform = 'none'
    buttonDefaults._disabled = {
      cursor: 'not-allowed',
      opacity: 1,
      transition: 'none',
      transform: 'none',
    }
  }

  switch (buttonType) {
    case 'primary':
      break

    case 'secondary':
      buttonDefaults.bg = 'transparent'
      buttonDefaults.color = !isDisabled ? 'gray.800' : 'gray.600'
      buttonDefaults.border = '1px solid gray.400'
      buttonDefaults._hover = !isDisabled
        ? {
            background: 'gray.100',
          }
        : {
            background: 'transparent',
            color: 'gray.600',
            opacity: 1,
            transform: 'none',
            transition: 'none',
          }
      break

    case 'danger':
      buttonDefaults.bg = !disabled ? 'red.500' : 'red.200'
      buttonDefaults.color = 'white'
      buttonDefaults.border = 'none'
      buttonDefaults._hover = {
        background: !disabled ? 'red.600' : 'red.200',
      }
      break

    default:
      buttonDefaults.bg = !isDisabled ? 'gray.800' : 'gray.300'
      buttonDefaults.color = !isDisabled ? 'gray.100' : 'gray.700'
      buttonDefaults.border = 'none'
      buttonDefaults._hover = !isDisabled
        ? {
            background: 'gray.700',
          }
        : {
            background: 'gray.300',
            color: 'gray.700',
            opacity: 1,
            transform: 'none',
            transition: 'none',
          }
  }

  return (
    <Button
      onClick={clickFn}
      className={className}
      isDisabled={isDisabled}
      disabled={isDisabled}
      {...buttonDefaults}
      {...rest}
      borderRadius={borderRadius}
    >
      {isButtonLoading ? (
        <div className="flex items-center gap-2">
          <Spinner color="white" loading />
          <span>{loadingText}</span>
        </div>
      ) : (
        <>{text || children}</>
      )}
    </Button>
  )
}

export default DefaultButton
